// Work-chat preferences on the layered settings API. The catalog key is
// registered at USER scope in ml-scribe (AGE-2360); an unset key resolves to
// `null`, which is a real state — "never picked" — not a stand-in for a mode.
import { getScribeHttpClients } from '@heidi/api/client';
import type { components } from '@heidi/schema/ml-scribe';

import type { ApprovalMode } from '../../contracts/harness.types';
import type { WorkRunSpeed } from '../../contracts/project-run-flags';

type SettingsCollectionResponse = components['schemas']['SettingsCollectionResponse'];

export const WORK_CHAT_APPROVAL_MODE_SETTING_KEY = 'work-chat_approval_mode';
export const WORK_CHAT_RUN_SPEED_SETTING_KEY = 'work-chat_run_speed';

interface ScribeHttp {
  get<T>(url: string): Promise<{ data: T }>;
  patch<T>(url: string, body?: unknown): Promise<{ data: T }>;
}

const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

// One get/patch site: a second settings patch call would clear the two-claim
// floor and hand `/settings/**` (the settings *pages*, plus middleware) to
// agents. Both catalog keys share this one collection endpoint.
const getSettingsCollection = async (
  key: string
): Promise<SettingsCollectionResponse> => {
  const response = await http().get<SettingsCollectionResponse>(`settings?keys=${key}`);
  return response.data;
};

const patchSettingsCollection = async (
  key: string,
  value: string
): Promise<SettingsCollectionResponse> => {
  const response = await http().patch<SettingsCollectionResponse>('settings', {
    [key]: value,
  });
  return response.data;
};

const toApprovalMode = (data: SettingsCollectionResponse): ApprovalMode | null => {
  const value = data.settings[WORK_CHAT_APPROVAL_MODE_SETTING_KEY]?.value;
  return value === 'auto' || value === 'manual' ? value : null;
};

export const getWorkChatApprovalMode = async (): Promise<ApprovalMode | null> =>
  toApprovalMode(await getSettingsCollection(WORK_CHAT_APPROVAL_MODE_SETTING_KEY));

export const setWorkChatApprovalMode = async (
  approvalMode: ApprovalMode
): Promise<ApprovalMode | null> =>
  toApprovalMode(
    await patchSettingsCollection(WORK_CHAT_APPROVAL_MODE_SETTING_KEY, approvalMode)
  );

const toRunSpeed = (data: SettingsCollectionResponse): WorkRunSpeed | null => {
  const value = data.settings[WORK_CHAT_RUN_SPEED_SETTING_KEY]?.value;
  return value === 'fast' || value === 'normal' ? value : null;
};

export const getWorkChatRunSpeed = async (): Promise<WorkRunSpeed | null> =>
  toRunSpeed(await getSettingsCollection(WORK_CHAT_RUN_SPEED_SETTING_KEY));

export const setWorkChatRunSpeed = async (
  speed: WorkRunSpeed
): Promise<WorkRunSpeed | null> =>
  toRunSpeed(await patchSettingsCollection(WORK_CHAT_RUN_SPEED_SETTING_KEY, speed));
